'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { forgotPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { forgotPasswordSchema } from '@eliadmin/core/src/components/auth/forgot-password-schema';
import Link from '@eliadmin/core/src/components/link';
import { TextField } from '@eliadmin/core/src/components/svetech/forms/fields';
import {
  AuthHeader,
  textLinkClassName,
} from '@eliadmin/core/src/components/svetech/forms/form-parts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export { forgotPasswordSchema } from '@eliadmin/core/src/components/auth/forgot-password-schema';

/**
 * „Forgotten password“ (core logic unchanged: e-mail → reset link, toast and back to sign-in):
 * H2 title, the e-mail field, the full-width submit and a green link back to sign-in.
 */
export function ForgotPasswordForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof forgotPasswordSchema>>>({
    resolver: zodResolver(forgotPasswordSchema(formValidationMessages)),
    defaultValues: {
      email: '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      const url = new URL(ROUTES.RESET_PASSWORD(), env.NEXT_PUBLIC_APP_URL);
      await forgotPassword({ ...data, return_url: url.toString(), locale });
      toast({
        title: t('text652'),
        variant: 'success',
      });
      router.push(ROUTES.SIGN_IN());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text651'),
        description: t('text3140'),
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        noValidate
        className={cn('flex w-full flex-col gap-6 md:gap-8', 'ea-password-reset-form')}
        onSubmit={handleSubmit}
      >
        <div className="ea-password-reset-form__header">
          <AuthHeader title={t('text188')} />
        </div>

        <TextField
          control={form.control}
          name="email"
          type="email"
          autoComplete="email"
          label={t('text35')}
          placeholder={t('text259')}
          required
          className="ea-password-reset-form__field-email"
        />

        <div className="ea-password-reset-form__footer flex flex-col items-center gap-4">
          <Button
            type="submit"
            className="ea-password-reset-form__submit-button w-full"
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')}
          </Button>
          <Link
            href={ROUTES.SIGN_IN() ?? '#'}
            className={cn('svt-body', textLinkClassName)}
            prefetch={false}
          >
            {t('text189')}
          </Link>
        </div>
      </form>
    </Form>
  );
}
